import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, MapPin, Users, Bed, Bath, Phone, Mail, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { DestinationsCarousel } from '../components/DestinationsCarousel';
import { LocationSection } from '../components/LocationSection';
import { getPropertyName, getPropertyDescription } from '../translations/propertyTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Menu translations
const menuTranslations = {
  properties: { fr: 'Propriétés', en: 'Properties', es: 'Propiedades', de: 'Unterkünfte', it: 'Proprietà', nl: 'Woningen', ru: 'Объекты', ar: 'العقارات', zh: '物业', ja: '物件', ko: '숙소', hi: 'संपत्तियां' },
  discover: { fr: 'Découvrir', en: 'Discover', es: 'Descubrir', de: 'Entdecken', it: 'Scopri', nl: 'Ontdekken', ru: 'Открыть', ar: 'اكتشف', zh: '探索', ja: '発見', ko: '탐험', hi: 'खोजें' },
  location: { fr: 'Localisation', en: 'Location', es: 'Ubicación', de: 'Standort', it: 'Posizione', nl: 'Locatie', ru: 'Местоположение', ar: 'الموقع', zh: '位置', ja: '所在地', ko: '위치', hi: 'स्थान' },
  contact: { fr: 'Contact', en: 'Contact', es: 'Contacto', de: 'Kontakt', it: 'Contatto', nl: 'Contact', ru: 'Контакт', ar: 'اتصل', zh: '联系', ja: '連絡先', ko: '연락처', hi: 'संपर्क' }
};

function HomePage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const villa = properties.find(p => p.type === 'villa');
  const apartments = properties.filter(p => p.type === 'apartment');

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Top Bar with Contact */}
      <div className="bg-primary text-primary-foreground py-1.5 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-end gap-4 text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <a 
              href="https://wa.me/33611518866" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2"
              title="Contact WhatsApp"
            >
              <MessageCircle size={16} className="text-green-600" fill="currentColor" />
            </a>
            <Phone size={14} />
            <a href="tel:+33611518866" className="hover:underline">+33 6 11 51 88 66</a>
          </div>
          <div className="flex items-center gap-2">
            <Mail size={14} />
            <a href="mailto:guiauchaindiane@orange.fr" className="hover:underline">guiauchaindiane@orange.fr</a>
          </div>
        </div>
      </div>

      {/* Navigation Header */}
      <div className="bg-white border-b border-border py-3 px-4 md:px-8 lg:px-12 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          {/* Logo / Site Name */}
          <div className="font-semibold text-lg md:text-xl text-primary">
            Côte d'Azur Stay
          </div>
          
          {/* Navigation Menu */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <button onClick={() => scrollTo('properties')} className="hover:text-accent transition-colors">
              {menuTranslations.properties[language] || 'Properties'}
            </button>
            <button onClick={() => scrollTo('discover')} className="hover:text-accent transition-colors">
              {menuTranslations.discover[language] || 'Discover'}
            </button>
            <button onClick={() => scrollTo('location')} className="hover:text-accent transition-colors">
              {menuTranslations.location[language] || 'Location'}
            </button>
            <button onClick={() => scrollTo('contact')} className="hover:text-accent transition-colors">
              {menuTranslations.contact[language] || 'Contact'}
            </button>
          </nav>

          {/* Language Selector */}
          <LanguageSelector />
        </div>
      </div>

      <section 
        className="hero-section min-h-screen flex items-end justify-center pb-32"
        style={{
          backgroundImage: 'url(https://i.ibb.co/svvGFjHW/2.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-content text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl text-white mb-20 drop-shadow-lg font-light tracking-wide">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-20 leading-relaxed font-light">
            {t('hero.subtitle')}
          </p>
          <div className="mt-12">
            <button
              data-testid="explore-properties-btn"
              className="group relative inline-block"
              onClick={() => document.getElementById('properties').scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="text-2xl md:text-3xl text-white font-light tracking-wide border-b-2 border-white/60 pb-2 hover:border-white transition-all duration-300 cursor-pointer">
                {t('hero.cta')}
              </span>
              <span className="block text-white/80 text-sm mt-2 font-light">↓</span>
            </button>
          </div>
        </div>
      </section>

      <section id="properties" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">{t('properties.title')}</h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {t('properties.subtitle')}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Roof Top Eze Penthouse en premier (featured) */}
              {apartments.find(a => a.id === 'apartment-2') && (
                <div className="lg:col-span-2" data-testid="property-penthouse">
                  <PropertyCard property={apartments.find(a => a.id === 'apartment-2')} featured t={t} language={language} />
                </div>
              )}
              {/* Villa */}
              {villa && (
                <div data-testid="property-villa">
                  <PropertyCard property={villa} t={t} language={language} />
                </div>
              )}
              {/* Front Beach apartment */}
              {apartments.find(a => a.id === 'apartment-1') && (
                <div data-testid="property-apartment-1">
                  <PropertyCard property={apartments.find(a => a.id === 'apartment-1')} t={t} language={language} />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Destinations Carousel Section */}
      <section id="discover" className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">
              {language === 'fr' ? 'Découvrez la Région' : 
               language === 'en' ? 'Discover the Region' :
               language === 'es' ? 'Descubre la Región' :
               language === 'de' ? 'Entdecken Sie die Region' :
               language === 'it' ? 'Scopri la Regione' :
               language === 'nl' ? 'Ontdek de Regio' :
               language === 'ru' ? 'Откройте для себя регион' :
               language === 'ar' ? 'اكتشف المنطقة' :
               language === 'zh' ? '探索该地区' :
               language === 'ja' ? '地域を発見' :
               language === 'ko' ? '지역 탐험' :
               language === 'hi' ? 'क्षेत्र की खोज करें' : 'Discover the Region'}
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {language === 'fr' ? 'Entre Monaco et Nice, profitez des plus beaux sites de la Côte d\'Azur' : 
               language === 'en' ? 'Between Monaco and Nice, enjoy the most beautiful sites of the French Riviera' :
               language === 'es' ? 'Entre Mónaco y Niza, disfruta de los lugares más bellos de la Costa Azul' :
               language === 'de' ? 'Zwischen Monaco und Nizza, genießen Sie die schönsten Orte der Côte d\'Azur' :
               language === 'it' ? 'Tra Monaco e Nizza, goditi i luoghi più belli della Costa Azzurra' :
               language === 'nl' ? 'Tussen Monaco en Nice, geniet van de mooiste plekken van de Côte d\'Azur' :
               language === 'ru' ? 'Между Монако и Ниццей, насладитесь красивейшими местами Лазурного берега' :
               language === 'ar' ? 'بين موناكو ونيس، استمتع بأجمل مواقع الريفييرا الفرنسية' :
               language === 'zh' ? '在摩纳哥和尼斯之间，欣赏蔚蓝海岸最美丽的景点' :
               language === 'ja' ? 'モナコとニースの間で、コートダジュールの最も美しい場所をお楽しみください' :
               language === 'ko' ? '모나코와 니스 사이에서 코트다쥐르의 가장 아름다운 명소를 즐기세요' :
               language === 'hi' ? 'मोनाको और नीस के बीच, फ्रेंच रिवेरा के सबसे खूबसूरत स्थलों का आनंद लें' : 'Between Monaco and Nice, enjoy the most beautiful sites of the French Riviera'}
            </p>
          </div>
          <DestinationsCarousel language={language} />
        </div>
      </section>

      {/* Location Section */}
      <LocationSection language={language} />

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-muted-foreground mb-12">
            {t('contact.subtitle')}
          </p>

          <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 dark:to-blue-950/20 rounded-2xl p-8 md:p-12 shadow-lg border border-sky-200 dark:border-sky-800">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Name */}
              <div className="flex items-center justify-center gap-4 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                <Home size={28} className="text-accent flex-shrink-0" />
                <p className="text-xl font-medium">Mlle Diane Guiauchain</p>
              </div>

              {/* Phone */}
              <div className="flex items-center justify-center gap-4 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                <Phone size={28} className="text-accent flex-shrink-0" />
                <a 
                  href="tel:+33611518866" 
                  className="text-xl font-medium hover:text-accent transition-colors"
                >
                  +33 6 11 51 88 66
                </a>
              </div>

              {/* WhatsApp */}
              <div className="flex items-center justify-center gap-4 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                <MessageCircle size={28} className="text-green-600 flex-shrink-0" fill="currentColor" />
                <a 
                  href="https://wa.me/33611518866" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xl font-medium text-green-600 hover:text-green-700 transition-colors"
                >
                  +33 6 11 51 88 66
                </a>
              </div>

              {/* Email */}
              <div className="flex items-center justify-center gap-4 p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm">
                <Mail size={28} className="text-accent flex-shrink-0" />
                <a 
                  href="mailto:guiauchaindiane@orange.fr" 
                  className="text-xl font-medium hover:text-accent transition-colors break-all"
                >
                  guiauchaindiane@orange.fr
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Section Multilingue - Découvrez Eze */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          
          {/* VERSION FRANÇAISE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Découvrez Eze sur la Côte d'Azur</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Location Vacances Eze</h3>
                <p className="text-muted-foreground">
                  Nos <strong>locations de vacances à Eze</strong> offrent des hébergements d'exception sur la <strong>Côte d'Azur</strong>. 
                  Que vous recherchiez une <strong>villa avec piscine</strong>, un <strong>appartement vue mer</strong> ou un 
                  <strong> penthouse de luxe</strong>, nos biens allient confort et vue époustouflante sur la Méditerranée. 
                  Idéal pour vos <strong>vacances d'été French Riviera</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Eze : Village Médiéval</h3>
                <p className="text-muted-foreground">
                  <strong>Eze village</strong> est un magnifique <strong>village médiéval</strong> perché entre Nice et Monaco. 
                  Découvrez la <strong>légende de la Chèvre d'Or</strong> et les <strong>fêtes médiévales</strong> du village. 
                  Le célèbre <strong>restaurant La Chèvre d'Or</strong> domine le village avec sa vue spectaculaire.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Sentier Nietzsche & Randonnées</h3>
                <p className="text-muted-foreground">
                  Notre <strong>maison du parc naturel</strong> est située sur le <strong>sentier de randonnée Friedrich Nietzsche</strong>, 
                  superbe balade reliant <strong>Eze-sur-Mer à Eze village</strong>. Chemins de randonnée panoramiques avec 
                  vue imprenable sur la Méditerranée.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Train & Gare à Proximité</h3>
                <p className="text-muted-foreground">
                  La <strong>gare SNCF Eze-sur-Mer</strong> est juste à côté de nos maisons ! Visitez toute la <strong>Côte d'Azur en train</strong> : 
                  Monaco, Nice, Cannes, Antibes, Menton. Accès direct sans voiture, écologique et pratique.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Proche Monaco & Italie</h3>
                <p className="text-muted-foreground">
                  À <strong>10 minutes de Monaco</strong> en voiture ou train. Visitez le <strong>marché de Vintimille</strong> en Italie, 
                  les <strong>plages italiennes</strong>, et la <strong>piste cyclable de 50 km depuis San Remo</strong> vers le sud. 
                  Frontière italienne à 30 minutes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Plages & Activités Marines</h3>
                <p className="text-muted-foreground">
                  La mythique <strong>plage de la Paloma à Saint-Jean-Cap-Ferrat</strong>, véritable <strong>Eden méditerranéen</strong>. 
                  <strong>Excursions whale watching</strong> (observation des baleines) au départ de <strong>Beaulieu-sur-Mer</strong>. 
                  Sports nautiques, plongée, voile.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION ANGLAISE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Discover Eze on the French Riviera</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Vacation Rentals Eze</h3>
                <p className="text-muted-foreground">
                  Our <strong>vacation rentals in Eze</strong> offer exceptional accommodations on the <strong>French Riviera</strong>. 
                  <strong>Villa with pool</strong>, <strong>sea view apartment</strong>, or <strong>luxury penthouse</strong> near Monaco and Nice.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Medieval Village</h3>
                <p className="text-muted-foreground">
                  <strong>Eze medieval village</strong> between Nice and Monaco. Discover the <strong>Golden Goat legend</strong> and 
                  <strong> medieval festivals</strong>. Famous <strong>La Chèvre d'Or restaurant</strong> with spectacular views.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Nietzsche Trail & Hiking</h3>
                <p className="text-muted-foreground">
                  Our <strong>natural park house</strong> is on the <strong>Friedrich Nietzsche hiking trail</strong>, 
                  stunning walk linking <strong>Eze-sur-Mer to Eze village</strong>. Panoramic hiking paths with breathtaking Mediterranean views.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Train Station Nearby</h3>
                <p className="text-muted-foreground">
                  <strong>Eze-sur-Mer train station</strong> right next to our homes! Visit the entire <strong>French Riviera by train</strong>: 
                  Monaco, Nice, Cannes, Antibes, Menton. Direct access without car, eco-friendly and convenient.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Close Monaco & Italy</h3>
                <p className="text-muted-foreground">
                  <strong>10 minutes from Monaco</strong> by car or train. Visit <strong>Vintimille market</strong> in Italy, 
                  <strong>Italian beaches</strong>, and the <strong>50 km cycling path from San Remo</strong>. Italian border 30 minutes away.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Beaches & Marine Activities</h3>
                <p className="text-muted-foreground">
                  The mythical <strong>Paloma Beach in Saint-Jean-Cap-Ferrat</strong>, a true <strong>Mediterranean Eden</strong>. 
                  <strong>Whale watching excursions</strong> from <strong>Beaulieu-sur-Mer</strong>. Water sports, diving, sailing.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION ALLEMANDE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Entdecken Sie Eze an der Côte d'Azur</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Ferienwohnungen Eze</h3>
                <p className="text-muted-foreground">
                  Unsere <strong>Ferienwohnungen in Eze</strong> bieten außergewöhnliche Unterkünfte an der <strong>Côte d'Azur</strong>. 
                  <strong>Villa mit Pool</strong>, <strong>Apartment mit Meerblick</strong>, <strong>Luxus-Penthouse</strong> in der Nähe von Monaco.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Mittelalterliches Dorf</h3>
                <p className="text-muted-foreground">
                  <strong>Eze mittelalterliches Dorf</strong> zwischen Nizza und Monaco. <strong>Ferienhäuser</strong> im Naturpark mit 
                  spektakulärem Mittelmeerblick. Perfekt für <strong>Sommerurlaub Französische Riviera</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Nähe Monaco</h3>
                <p className="text-muted-foreground">
                  <strong>Unterkunft am Meer</strong> 10 Minuten von <strong>Monaco</strong>, 15 Minuten von <strong>Nizza</strong>. 
                  Strände, Monte-Carlo Casino, Wanderwege, <strong>Tourismus Côte d'Azur</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION ITALIENNE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Scopri Eze sulla Costa Azzurra</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Affitti Vacanze Eze</h3>
                <p className="text-muted-foreground">
                  I nostri <strong>affitti vacanze a Eze</strong> offrono alloggi eccezionali sulla <strong>Costa Azzurra</strong>. 
                  <strong>Villa con piscina</strong>, <strong>appartamento vista mare</strong>, <strong>attico di lusso</strong> vicino Monaco.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Villaggio Medievale</h3>
                <p className="text-muted-foreground">
                  <strong>Eze villaggio medievale</strong> tra Nizza e Monaco. <strong>Case vacanza</strong> nel parco naturale con 
                  vista spettacolare sul Mediterraneo. Perfetto per <strong>vacanze estive Riviera Francese</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Vicino Monaco</h3>
                <p className="text-muted-foreground">
                  <strong>Alloggio sul mare</strong> a 10 minuti da <strong>Monaco</strong>, 15 minuti da <strong>Nizza</strong>. 
                  Spiagge, casinò Monte-Carlo, sentieri escursionistici, <strong>turismo Costa Azzurra</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION ESPAGNOLE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Descubre Eze en la Costa Azul</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Alquileres Vacacionales Eze</h3>
                <p className="text-muted-foreground">
                  Nuestros <strong>alquileres vacacionales en Eze</strong> ofrecen alojamientos excepcionales en la <strong>Costa Azul</strong>. 
                  <strong>Villa con piscina</strong>, <strong>apartamento con vista al mar</strong>, <strong>ático de lujo</strong> cerca de Mónaco.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Pueblo Medieval</h3>
                <p className="text-muted-foreground">
                  <strong>Eze pueblo medieval</strong> entre Niza y Mónaco. <strong>Casas de vacaciones</strong> en parque natural con 
                  vistas espectaculares al Mediterráneo. Perfecto para <strong>vacaciones de verano Riviera Francesa</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Cerca de Mónaco</h3>
                <p className="text-muted-foreground">
                  <strong>Alojamiento junto al mar</strong> a 10 minutos de <strong>Mónaco</strong>, 15 minutos de <strong>Niza</strong>. 
                  Playas, casino Monte-Carlo, rutas de senderismo, <strong>turismo Costa Azul</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION RUSSE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">Откройте для себя Эз на Лазурном Берегу</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Аренда Жилья Эз</h3>
                <p className="text-muted-foreground">
                  Наша <strong>аренда на отдых в Эз</strong> предлагает исключительное жилье на <strong>Лазурном Берегу</strong>. 
                  <strong>Вилла с бассейном</strong>, <strong>квартира с видом на море</strong>, <strong>роскошный пентхаус</strong> рядом с Монако.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Средневековая Деревня</h3>
                <p className="text-muted-foreground">
                  <strong>Средневековая деревня Эз</strong> между Ниццей и Монако. <strong>Дома для отдыха</strong> в природном парке с 
                  захватывающим видом на Средиземное море. Идеально для <strong>летнего отдыха на Французской Ривьере</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">Рядом с Монако</h3>
                <p className="text-muted-foreground">
                  <strong>Жилье у моря</strong> в 10 минутах от <strong>Монако</strong>, в 15 минутах от <strong>Ниццы</strong>. 
                  Пляжи, казино Монте-Карло, пешеходные маршруты, <strong>туризм Лазурный Берег</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION ARABE */}
          <div className="mb-16" dir="rtl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">اكتشف إيز على الريفييرا الفرنسية</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">إيجارات العطلات في إيز</h3>
                <p className="text-muted-foreground">
                  تقدم <strong>إيجاراتنا في إيز</strong> إقامات استثنائية على <strong>الريفييرا الفرنسية</strong>. 
                  <strong>فيلا مع مسبح</strong>، <strong>شقة بإطلالة على البحر</strong>، <strong>بنتهاوس فاخر</strong> بالقرب من موناكو ونيس.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">قرية العصور الوسطى</h3>
                <p className="text-muted-foreground">
                  <strong>قرية إيز التاريخية</strong> بين نيس وموناكو. <strong>منازل العطلات</strong> في المنتزه الطبيعي مع 
                  إطلالات خلابة على البحر الأبيض المتوسط. مثالية <strong>لعطلات الصيف على الريفييرا الفرنسية</strong>.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">قريب من موناكو</h3>
                <p className="text-muted-foreground">
                  <strong>إقامة على البحر</strong> على بعد 10 دقائق من <strong>موناكو</strong>، 15 دقيقة من <strong>نيس</strong>. 
                  شواطئ، كازينو مونت كارلو، مسارات المشي، <strong>سياحة كوت دازور</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION CHINOISE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">探索法国蔚蓝海岸的埃兹</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">埃兹度假租赁</h3>
                <p className="text-muted-foreground">
                  我们在<strong>埃兹的度假租赁</strong>提供<strong>法国蔚蓝海岸</strong>的卓越住宿体验。
                  <strong>带泳池的别墅</strong>、<strong>海景公寓</strong>、<strong>豪华顶层公寓</strong>，靠近摩纳哥和尼斯。
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">中世纪村庄</h3>
                <p className="text-muted-foreground">
                  <strong>埃兹中世纪村庄</strong>位于尼斯和摩纳哥之间。<strong>度假屋</strong>坐落在自然公园内，
                  拥有壮观的地中海美景。非常适合<strong>法国里维埃拉的夏季度假</strong>。
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">靠近摩纳哥</h3>
                <p className="text-muted-foreground">
                  <strong>海滨住宿</strong>距离<strong>摩纳哥</strong>10分钟，距离<strong>尼斯</strong>15分钟。
                  海滩、蒙特卡洛赌场、远足小径、<strong>蔚蓝海岸旅游</strong>。
                </p>
              </div>
            </div>
          </div>

          {/* VERSION CORÉENNE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">프랑스 리비에라의 에즈를 발견하세요</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">에즈 휴가 렌탈</h3>
                <p className="text-muted-foreground">
                  <strong>에즈의 휴가 렌탈</strong>은 <strong>프랑스 리비에라</strong>에서 특별한 숙박을 제공합니다. 
                  <strong>수영장이 있는 빌라</strong>, <strong>바다 전망 아파트</strong>, <strong>럭셔리 펜트하우스</strong>, 모나코와 니스 근처.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">중세 마을</h3>
                <p className="text-muted-foreground">
                  <strong>에즈 중세 마을</strong>은 니스와 모나코 사이에 위치해 있습니다. <strong>휴가용 주택</strong>은 자연 공원 내에 있으며 
                  지중해의 장관을 감상할 수 있습니다. <strong>프렌치 리비에라 여름 휴가</strong>에 완벽합니다.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">모나코 근처</h3>
                <p className="text-muted-foreground">
                  <strong>해변가 숙소</strong>에서 <strong>모나코</strong>까지 10분, <strong>니스</strong>까지 15분. 
                  해변, 몬테카를로 카지노, 하이킹 코스, <strong>코트다쥐르 관광</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* VERSION HINDI (INDIEN) */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">फ्रेंच रिवेरा पर एज़ की खोज करें</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">एज़ वेकेशन रेंटल</h3>
                <p className="text-muted-foreground">
                  <strong>एज़ में हमारे वेकेशन रेंटल</strong> <strong>फ्रेंच रिवेरा</strong> पर असाधारण आवास प्रदान करते हैं। 
                  <strong>पूल के साथ विला</strong>, <strong>समुद्र दृश्य अपार्टमेंट</strong>, <strong>लक्जरी पेंटहाउस</strong> मोनाको और नीस के पास।
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">मध्यकालीन गांव</h3>
                <p className="text-muted-foreground">
                  <strong>एज़ मध्यकालीन गांव</strong> नीस और मोनाको के बीच। <strong>छुट्टी के घर</strong> प्राकृतिक पार्क में 
                  भूमध्य सागर के शानदार दृश्यों के साथ। <strong>फ्रेंच रिवेरा पर गर्मी की छुट्टियों</strong> के लिए आदर्श।
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">मोनाको के पास</h3>
                <p className="text-muted-foreground">
                  <strong>समुद्र के किनारे आवास</strong> <strong>मोनाको</strong> से 10 मिनट, <strong>नीस</strong> से 15 मिनट। 
                  समुद्र तट, मोंटे कार्लो कैसीनो, लंबी पैदल यात्रा के रास्ते, <strong>कोट डी'आज़ूर पर्यटन</strong>।
                </p>
              </div>
            </div>
          </div>

          {/* VERSION JAPONAISE */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">フレンチリビエラのエズを発見</h2>
              <div className="w-20 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm md:text-base leading-relaxed">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">エズのバケーションレンタル</h3>
                <p className="text-muted-foreground">
                  <strong>エズのバケーションレンタル</strong>は、<strong>フレンチリビエラ</strong>で特別な宿泊体験を提供します。
                  <strong>プール付きヴィラ</strong>、<strong>オーシャンビューアパートメント</strong>、<strong>高級ペントハウス</strong>、モナコとニース近郊。
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">中世の村</h3>
                <p className="text-muted-foreground">
                  <strong>エズの中世の村</strong>はニースとモナコの間に位置しています。<strong>バケーションホーム</strong>は自然公園内にあり、
                  地中海の壮大な景色を楽しめます。<strong>フレンチリビエラでの夏休み</strong>に最適です。
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-accent">モナコ近郊</h3>
                <p className="text-muted-foreground">
                  <strong>海辺の宿泊施設</strong>から<strong>モナコ</strong>まで10分、<strong>ニース</strong>まで15分。
                  ビーチ、モンテカルロカジノ、ハイキングコース、<strong>コートダジュール観光</strong>。
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <footer className="bg-primary text-primary-foreground py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm">&copy; 2025 Vacation Rentals. {t('footer.rights')}</p>
        </div>
      </footer>
    </div>
  );
}

function PropertyCard({ property, featured, t, language }) {
  const translatedName = getPropertyName(property.id, language) || property.name;
  const translatedDescription = getPropertyDescription(property.id, language) || property.description;
  
  return (
    <Card className="group relative overflow-hidden rounded-xl bg-white border border-border shadow-sm hover:shadow-md transition-all duration-300">
      <Link to={`/property/${property.id}`}>
        <div className="relative overflow-hidden">
          <img
            src={property.images[0]}
            alt={translatedName}
            className="property-card-image"
          />
          {property.type === 'villa' && (
            <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium">
              {t('properties.villa')}
            </div>
          )}
        </div>
        <CardContent className="p-6">
          <h3 className="text-2xl font-medium mb-3">{translatedName}</h3>
          <p className="text-muted-foreground mb-4 line-clamp-2">{translatedDescription}</p>
          
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Users size={16} />
              <span>{property.max_guests} {t('properties.guests')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Bed size={16} />
              <span>{property.bedrooms} {t('properties.bedrooms')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Bath size={16} />
              <span>{property.bathrooms} {t('properties.bathrooms')}</span>
            </div>
          </div>

          <div className="flex items-center justify-end pt-4 border-t border-border">
            <Button 
              data-testid={`view-property-btn-${property.id}`}
              variant="outline" 
              className="rounded-full"
            >
              {t('properties.viewDetails')}
            </Button>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}

export default HomePage;